import React, { useEffect, useState } from "react";
import { Phone, MessageCircle, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import HubSpotForm from "./HubSpotForm";

const DEFAULT_EMERGENCY = "+1 315 897 5589";

function formatPhone(raw) {
  if (!raw) return "";
  if (raw.includes(" ")) return raw;
  const m = raw.replace(/[^\d]/g, "").match(/^1?(\d{3})(\d{3})(\d{4})$/);
  if (!m) return raw;
  return `+1 ${m[1]} ${m[2]} ${m[3]}`;
}

/**
 * GuestContactForm — header + always-visible emergency banner with call/text
 * links. The form body itself is now the unified Janté HubSpot embed so every
 * inbound lead routes to HubSpot instead of the legacy custom endpoint.
 */
export default function GuestContactForm() {
  const [emergencyPhone, setEmergencyPhone] = useState(DEFAULT_EMERGENCY);

  useEffect(() => {
    api
      .get("/guest-contact/config")
      .then((r) => {
        if (r.data?.emergency_phone) setEmergencyPhone(r.data.emergency_phone);
      })
      .catch(() => {});
  }, []);

  const telHref = `tel:${emergencyPhone.replace(/[^\d+]/g, "")}`;
  const smsHref = `sms:${emergencyPhone.replace(/[^\d+]/g, "")}`;
  const displayPhone = formatPhone(emergencyPhone);

  return (
    <div className="lux-card" data-testid="guest-contact-form">
      <div className="overline">Guest care</div>
      <h3 className="font-display text-4xl text-[var(--emerald)] mt-3">
        Reach a concierge.
      </h3>
      <p className="text-sm text-[var(--ink-2)] mt-3 max-w-xl">
        Current guests, prospective travelers, and anyone needing urgent help — we're one
        message away.
      </p>

      <div
        className="mt-6 border border-[var(--brand-red)]/40 bg-[var(--brand-red)]/5 p-5"
        data-testid="guest-emergency-banner"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle size={18} strokeWidth={1.5} className="text-[var(--brand-red)] mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--brand-red)]">
              In case of emergency
            </div>
            <p className="mt-1 text-sm text-[var(--ink)]">
              Call or text{" "}
              <a
                href={telHref}
                className="font-display text-lg text-[var(--brand-red)] hover:underline"
                data-testid="guest-emergency-phone"
              >
                {displayPhone}
              </a>{" "}
              — our guest-emergency line.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <a
                href={telHref}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] bg-[var(--brand-red)] text-[var(--ivory)] hover:opacity-90"
                data-testid="guest-emergency-call"
              >
                <Phone size={12} strokeWidth={1.75} /> Call now
              </a>
              <a
                href={smsHref}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] border border-[var(--brand-red)] text-[var(--brand-red)] hover:bg-[var(--brand-red)] hover:text-[var(--ivory)]"
                data-testid="guest-emergency-text"
              >
                <MessageCircle size={12} strokeWidth={1.75} /> Text now
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <HubSpotForm context="guest-contact" />
      </div>
    </div>
  );
}
